import {
  Button,
  ButtonLoading,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
} from '@data-delivery/d3s';
import type { PermissionApi } from '@data-delivery/permission-ui-api';
import {
  useBusinessGroupsRQ,
  useCreateAssignmentMutation,
  useProductsRQ,
  useUpdateAssignmentMutation,
} from '@data-delivery/permission-ui-data-access-state';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { z } from 'zod';
import { showErrorToast } from '../../utils/error-utils';
import { ComposableSheet } from '../sheets/composable-sheet';
import type { ChangeSnapshot } from './build-change-snapshot';
import { buildChangeSnapshot } from './build-change-snapshot';
import { ChangeReviewOverlay } from './change-review-overlay';
import { FieldBusinessGroupSelect } from './field-business-group-select';
import { FieldDateRange } from './field-date-range';
import { FieldServicesSelect } from './field-services-select';
import { FieldsBasicInfo } from './fields-basic-info';
import { formSchema } from './form-schema';
import { ProductsSection } from './products-section';

export type AssignmentEditorMode = 'create' | 'edit';

interface AssignmentEditorProps {
  mode: AssignmentEditorMode;
  initial?: PermissionApi.AssignmentEntity | undefined;
  allowBusinessGroupSelection?: boolean;
  overrideBusinessGroupId?: string;
}

export function AssignmentEditor({
  mode,
  initial,
  allowBusinessGroupSelection,
  overrideBusinessGroupId,
}: AssignmentEditorProps) {
  const [reviewingChanges, setReviewingChanges] = useState(false);
  const [changeSnapshot, setChangeSnapshot] = useState<ChangeSnapshot | undefined>(undefined);

  const navigate = useNavigate();
  const location = useLocation();
  const { businessGroupId, assignmentId } = useParams<{
    businessGroupId: string;
    assignmentId?: string;
  }>();

  const createMutation = useCreateAssignmentMutation();
  const updateMutation = useUpdateAssignmentMutation();
  const { data: businessGroupsData } = useBusinessGroupsRQ();
  const { data: productsData } = useProductsRQ();
  const businessGroupOptions = (businessGroupsData || []).map(bg => ({
    value: bg.id,
    label: (bg as { name?: string; shortName?: string }).name || bg.shortName || bg.id,
  }));

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      contract: initial?.contract ?? '',
      startDate: initial?.startDate ? new Date(initial.startDate).toISOString().slice(0, 10) : '',
      endDate: initial?.endDate ? new Date(initial.endDate).toISOString().slice(0, 10) : '',
      businessGroup: overrideBusinessGroupId || businessGroupId || initial?.businessGroup || '',
      products: initial?.products ?? [],
      services: initial?.services ?? [],
    },
  });

  const { handleSubmit, watch, setValue } = form;
  const productsSelected = watch('products') || [];
  const bgSelected = watch('businessGroup');

  function close() {
    const maybeState = location.state as unknown;
    const backgroundLocation =
      maybeState && typeof maybeState === 'object' && 'backgroundLocation' in maybeState
        ? (maybeState as { backgroundLocation?: Location }).backgroundLocation
        : undefined;
    if (backgroundLocation) {
      navigate(backgroundLocation.pathname + backgroundLocation.search + backgroundLocation.hash, {
        replace: true,
      });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    if (businessGroupId) {
      navigate(
        `/business-groups/${encodeURIComponent(businessGroupId)}/assignments/${
          assignmentId ? encodeURIComponent(assignmentId) : ''
        }`.replace(/\/assignments\/$/, '/assignments'),
      );
    } else {
      navigate('/configuration/assignments');
    }
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    const payload = {
      name: values.name,
      description: values.description,
      contract: values.contract,
      startDate: values.startDate ? new Date(values.startDate).getTime() : undefined,
      endDate:
        values.endDate === ''
          ? ''
          : values.endDate
          ? new Date(values.endDate).getTime()
          : undefined,
      businessGroup: values.businessGroup,
      products: values.products,
      services: values.services?.length ? values.services : [],
    };
    if (mode === 'create') {
      createMutation.mutate(payload as PermissionApi.CreateAssignmentDto, {
        onSettled: close,
        onError(err) {
          showErrorToast(err);
        },
      });
    } else if (mode === 'edit' && initial) {
      const changes: PermissionApi.UpdateAssignmentDto = {
        ...(payload as PermissionApi.UpdateAssignmentDto),
        products: values.products,
        lastModified: initial.lastModified,
      };
      updateMutation.mutate(
        { id: initial.id, changes },
        {
          onSettled: close,
          onError(err) {
            showErrorToast(err);
          },
        },
      );
    }
  }

  return (
    <ComposableSheet
      open
      deferClose
      onOpenChange={o => {
        if (!o) {
          close();
        }
      }}
      title={mode === 'create' ? 'Create Assignment' : 'Edit Assignment'}
      description='Provide assignment details.'
      widthClassName='w-full sm:max-w-5xl'
      side='right'
      closeDelayMs={60}
      footer={
        <>
          {createMutation.isPending || updateMutation.isPending ? (
            <ButtonLoading />
          ) : (
            <Button
              type='button'
              form='assignment-form'
              disabled={createMutation.isPending || updateMutation.isPending}
              onClick={async () => {
                if (mode === 'create') {
                  handleSubmit(onSubmit)();
                  return;
                }

                const isValid = await form.trigger();
                if (!isValid) {
                  return;
                }

                const values = form.getValues();
                const snapshot = buildChangeSnapshot(initial, values, {
                  businessGroups: businessGroupsData,
                  products: productsData,
                });
                if (!snapshot || snapshot.changedCount === 0) {
                  handleSubmit(onSubmit)();
                  return;
                }

                setChangeSnapshot(snapshot);
                setReviewingChanges(true);
              }}
            >
              {mode === 'edit' ? 'Review & Save' : 'Save'}
            </Button>
          )}
          <Button
            variant='outline'
            disabled={createMutation.isPending || updateMutation.isPending}
            onClick={close}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Form {...form}>
        <form
          id='assignment-form'
          onSubmit={handleSubmit(onSubmit)}
          className='relative flex flex-col gap-6 p-4'
        >
          <div className='grid gap-4 md:grid-cols-2'>
            <FieldsBasicInfo form={form} />
            <FieldDateRange
              form={form}
              pending={createMutation.isPending || updateMutation.isPending}
            />
            <FieldServicesSelect
              form={form}
              disabled={createMutation.isPending || updateMutation.isPending}
            />
            <FieldBusinessGroupSelect
              form={form}
              options={businessGroupOptions}
              mode={mode}
              allowBusinessGroupSelection={allowBusinessGroupSelection}
            />
            <ProductsSection
              selected={productsSelected}
              businessGroupId={bgSelected}
              isEditMode={mode === 'edit'}
              pending={createMutation.isPending || updateMutation.isPending}
              onChange={next => {
                setValue('products', next, { shouldValidate: true });
              }}
            />
          </div>
          <Dialog open={reviewingChanges} onOpenChange={setReviewingChanges}>
            <DialogContent className='max-w-5xl max-h-[85vh] flex flex-col'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>Review Changes</DialogTitle>
                <DialogDescription>
                  {changeSnapshot && changeSnapshot.changedCount > 0
                    ? `You're about to modify ${changeSnapshot.changedCount} ${
                        changeSnapshot.changedCount === 1 ? 'item' : 'items'
                      }. Please review carefully before confirming.`
                    : 'No changes detected.'}
                </DialogDescription>
              </DialogHeader>
              {changeSnapshot && (
                <ChangeReviewOverlay
                  snapshot={changeSnapshot}
                  loading={createMutation.isPending || updateMutation.isPending}
                  onBack={() => {
                    setReviewingChanges(false);
                    setChangeSnapshot(undefined);
                  }}
                  onConfirm={() => handleSubmit(onSubmit)()}
                />
              )}
            </DialogContent>
          </Dialog>
        </form>
      </Form>
    </ComposableSheet>
  );
}

export default AssignmentEditor;
